import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type {
  AstroSystem,
  AyanamsaId,
  BirthInput,
  HoroscopePeriod,
  HouseSystem,
  LifeArea,
} from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { localdb } from '../../lib/localdb';
import { getFirebase } from '../../lib/firebase';
import type { ComputedReading, ReaderPlace } from './horoscopeData';

/**
 * The accuracy loop — the habit hook.
 *
 * Design rule stated first because everything else follows from it: RATING IS
 * ONE TAP AND NEVER BLOCKS ON THE NETWORK. The verdict lands in React state
 * synchronously, is written to IndexedDB right after, and only then does a
 * best-effort sync run in the background. Nothing the user sees waits on a
 * promise, and a plane, a tunnel or demo mode changes nothing about the tap.
 *
 * LAYERING NOTE (please read before "fixing" this):
 * this module talks to the `accuracyRate` callable directly instead of going
 * through `Backend` (app/src/lib/backend/types.ts). That interface is owned by
 * another surface and had no horoscope methods when this screen was written.
 * When `Backend` is next opened, move `materialiseReading` and `postRating`
 * onto it and delete the firebase import here — nothing else in this file
 * changes.
 *
 * PROVENANCE, and why a rating needs two calls:
 * the server refuses to file a rating for a signal it cannot prove it produced
 * for this user (functions/src/journal/journalFns.ts loads
 * `horoscopes/{uid}/items/{readingKey}` and looks the signal up). This screen
 * computes readings on-device for speed, so the server has never seen one.
 * `materialiseReading` therefore asks `horoscopeGet` for the same window —
 * deterministic, no AI, one document read once per reading — and only then
 * posts the verdict. If any of that fails, the rating simply stays local: the
 * counts the user sees are their own file, not the server's.
 */

/** What the user taps. Mapped to the server's vocabulary on the way out. */
export type RateVerdict = 'hit' | 'partial' | 'miss';

export const RATE_OPTIONS: { verdict: RateVerdict; label: string; hint: string }[] = [
  { verdict: 'hit', label: 'Landed', hint: 'This described something real for me' },
  { verdict: 'partial', label: 'Partly', hint: 'Right theme, wrong size or wrong corner of life' },
  { verdict: 'miss', label: 'Missed', hint: 'Nothing like my period' },
];

/** functions/src/journal/journalFns.ts speaks landed / partly / missed. */
const WIRE_VERDICT: Record<RateVerdict, 'landed' | 'partly' | 'missed'> = {
  hit: 'landed',
  partial: 'partly',
  miss: 'missed',
};

export interface StoredRating {
  readingKey: string;
  signalId: string;
  signalLabel: string;
  verdict: RateVerdict;
  system: AstroSystem;
  period: HoroscopePeriod;
  area: LifeArea;
  ratedAtISO: string;
  /** 'local' = we stopped trying to sync it. The verdict itself is never lost. */
  sync: 'pending' | 'synced' | 'local';
  attempts: number;
}

export type RatingMap = Record<string, StoredRating>;

export function ratingKey(readingKey: string, signalId: string): string {
  return `${readingKey}|${signalId}`;
}

function storeKey(uid: string): string {
  return `horoAccuracy:${uid}`;
}

/** After this many failed sync attempts the rating is kept local and left alone. */
const MAX_SYNC_ATTEMPTS = 3;

// ---------------------------------------------------------------------------
// Server bridge
// ---------------------------------------------------------------------------

/** Everything `horoscopeGet` needs to reproduce the reading the user is rating. */
export interface SyncContext {
  birth: BirthInput;
  here: ReaderPlace;
  ayanamsaId: AyanamsaId;
  houseSystem: HouseSystem;
}

interface HoroscopeGetRequest {
  system: AstroSystem;
  period: HoroscopePeriod;
  periodKey: string;
  birth: BirthInput;
  here: { lat: number; lon: number; timezone: string };
  ayanamsaId: AyanamsaId;
  houseSystem: HouseSystem;
  narrate: false;
}

interface AccuracyRateRequest {
  readingKey: string;
  signalId: string;
  verdict: 'landed' | 'partly' | 'missed';
  tzOffsetMinutes: number;
}

/** Minutes east of UTC that `timezone` is at `at`. Gives the server the reader's day. */
export function tzOffsetMinutes(timezone: string, at: Date = new Date()): number {
  try {
    const parts: Record<string, number> = {};
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    for (const p of dtf.formatToParts(at)) if (p.type !== 'literal') parts[p.type] = Number(p.value);
    const asUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour % 24,
      parts.minute,
      parts.second,
    );
    return Math.round((asUtc - at.getTime()) / 60000);
  } catch {
    return 0;
  }
}

export function useAccuracy() {
  const { uid, backend } = useApp();
  const [ratings, setRatings] = useState<RatingMap>({});
  // The map is also held in a ref: two taps in the same tick would both read a
  // stale copy off state and the second would drop the first.
  const ratingsRef = useRef<RatingMap>({});
  const loadedFor = useRef<string | null>(null);
  /** readingKeys already pushed to the server this session — one call, not one per signal. */
  const materialised = useRef(new Set<string>());

  useEffect(() => {
    if (!uid) {
      ratingsRef.current = {};
      setRatings({});
      loadedFor.current = null;
      return;
    }
    if (loadedFor.current === uid) return;
    loadedFor.current = uid;
    let alive = true;
    localdb
      .get<RatingMap>('kv', storeKey(uid))
      .then((saved) => {
        if (!alive || !saved) return;
        ratingsRef.current = saved;
        setRatings(saved);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [uid]);

  const persist = useCallback(
    (next: RatingMap) => {
      if (!uid) return;
      void localdb.set('kv', storeKey(uid), next).catch(() => undefined);
    },
    [uid],
  );

  const markSync = useCallback(
    (key: string, sync: StoredRating['sync'], attempts: number) => {
      const current = ratingsRef.current[key];
      if (!current) return;
      const next: RatingMap = { ...ratingsRef.current, [key]: { ...current, sync, attempts } };
      ratingsRef.current = next;
      setRatings(next);
      persist(next);
    },
    [persist],
  );

  /**
   * Fire-and-forget. Every failure path ends in `markSync('local')` — a rating
   * the server would not take is still the user's rating.
   */
  const sync = useCallback(
    async (entry: StoredRating, reading: ComputedReading, ctx: SyncContext) => {
      const key = ratingKey(entry.readingKey, entry.signalId);
      if (backend.kind !== 'firebase') {
        markSync(key, 'local', entry.attempts);
        return;
      }
      const attempts = entry.attempts + 1;
      try {
        const { fns } = getFirebase();
        if (!materialised.current.has(entry.readingKey)) {
          const get = httpsCallable<HoroscopeGetRequest, unknown>(fns, 'horoscopeGet');
          await get({
            system: reading.system,
            period: reading.period,
            periodKey: reading.periodKey,
            birth: ctx.birth,
            here: { lat: ctx.here.lat, lon: ctx.here.lon, timezone: ctx.here.timezone },
            ayanamsaId: ctx.ayanamsaId,
            houseSystem: ctx.houseSystem,
            narrate: false,
          });
          materialised.current.add(entry.readingKey);
        }
        const rate = httpsCallable<AccuracyRateRequest, unknown>(fns, 'accuracyRate');
        await rate({
          readingKey: entry.readingKey,
          signalId: entry.signalId,
          verdict: WIRE_VERDICT[entry.verdict],
          tzOffsetMinutes: tzOffsetMinutes(ctx.here.timezone),
        });
        markSync(key, 'synced', attempts);
      } catch {
        markSync(key, attempts >= MAX_SYNC_ATTEMPTS ? 'local' : 'pending', attempts);
      }
    },
    [backend.kind, markSync],
  );

  /**
   * One tap. Synchronous by contract — the caller re-renders with the new
   * verdict before this returns, and nothing here awaits anything.
   */
  const rate = useCallback(
    (args: {
      reading: ComputedReading;
      signalId: string;
      signalLabel: string;
      area: LifeArea;
      verdict: RateVerdict;
      ctx: SyncContext;
    }) => {
      const { reading, signalId, signalLabel, area, verdict, ctx } = args;
      const key = ratingKey(reading.readingKey, signalId);
      const previous = ratingsRef.current[key];
      // Tapping the verdict already showing clears it — the only way back out
      // of a mis-tap, and cheaper than a separate undo affordance.
      if (previous && previous.verdict === verdict) {
        const next = { ...ratingsRef.current };
        delete next[key];
        ratingsRef.current = next;
        setRatings(next);
        persist(next);
        return;
      }
      const entry: StoredRating = {
        readingKey: reading.readingKey,
        signalId,
        signalLabel,
        verdict,
        system: reading.system,
        period: reading.period,
        area,
        ratedAtISO: new Date().toISOString(),
        sync: 'pending',
        attempts: 0,
      };
      const next: RatingMap = { ...ratingsRef.current, [key]: entry };
      ratingsRef.current = next;
      setRatings(next);
      persist(next);
      void sync(entry, reading, ctx);
    },
    [persist, sync],
  );

  const verdictFor = useCallback(
    (readingKey: string, signalId: string): RateVerdict | undefined =>
      ratings[ratingKey(readingKey, signalId)]?.verdict,
    [ratings],
  );

  return { ratings, rate, verdictFor, ratedCount: Object.keys(ratings).length };
}

// ---------------------------------------------------------------------------
// Local roll-up — what the user sees growing
// ---------------------------------------------------------------------------

export interface LocalTally {
  rated: number;
  hits: number;
  partials: number;
  misses: number;
  /** (hits + partials/2) / rated, 0..1. Matches `accuracyHitRate` in @astrally/shared. */
  hitRate: number;
}

/**
 * Rolled up on-device from the ratings this user has actually made. It is a
 * record of how often a lens FELT true to them — never evidence that astrology
 * predicts anything, and no surface may frame it as such.
 */
export function tally(ratings: RatingMap, filter?: (r: StoredRating) => boolean): LocalTally {
  let hits = 0;
  let partials = 0;
  let misses = 0;
  for (const r of Object.values(ratings)) {
    if (filter && !filter(r)) continue;
    if (r.verdict === 'hit') hits++;
    else if (r.verdict === 'partial') partials++;
    else misses++;
  }
  const rated = hits + partials + misses;
  return { rated, hits, partials, misses, hitRate: rated === 0 ? 0 : (hits + partials / 2) / rated };
}
